import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { Planet } from '@map/shared';
import { useAtmosphereContext } from '../atmosphere/AtmosphereProvider.js';
import { planetTraits } from '../globe/planetTraits.js';
import { createPlanet, renameUniverse, updatePlanet } from './api.js';

/**
 * The star is the system's settings: what this universe is called, what is in
 * orbit around it, and what it sounds like to sit here.
 */
export interface StarDialogProps {
  universeName: string;
  planets: Planet[];
  onClose: () => void;
}

/**
 * A seed for each look in the palette, found once by searching. Picking a
 * colour has to set the seed, because the seed is what the planet is drawn
 * from — and it must stay a seed, so the planet's tilt, rings and moons keep
 * coming from the same single number.
 */
function seedsByLook(): number[] {
  const found = new Map<string, number>();
  for (let seed = 1; seed < 20000 && found.size < 9; seed += 1) {
    const key = planetTraits(seed).palette.equator;
    if (!found.has(key)) found.set(key, seed);
  }
  return [...found.values()];
}

export function StarDialog({ universeName, planets, onClose }: StarDialogProps) {
  const queryClient = useQueryClient();
  // Shared with the whole app, so the sound carries on when this closes.
  const atmosphere = useAtmosphereContext();
  const looks = useMemo(seedsByLook, []);
  const [name, setName] = useState(universeName);
  const [newPlanet, setNewPlanet] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['universe'] });

  const rename = useMutation({ mutationFn: (value: string) => renameUniverse(value), onSuccess: refresh });
  const patch = useMutation({
    mutationFn: (input: { id: string; body: Parameters<typeof updatePlanet>[1] }) =>
      updatePlanet(input.id, input.body),
    onSuccess: refresh,
  });
  const add = useMutation({
    mutationFn: (value: string) => createPlanet(value, 'terra', 3),
    onSuccess: async () => {
      setNewPlanet('');
      await refresh();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(3,6,12,0.72)' }}>
      <div
        className="flex max-h-[88vh] w-full max-w-2xl flex-col gap-6 overflow-y-auto rounded-[var(--radius-md)] p-6"
        style={{ background: 'var(--surface-0)', border: '1px solid var(--border)' }}
        role="dialog"
        aria-label="System settings"
      >
        <header className="flex items-center justify-between">
          <h2 className="text-lg">The system</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text" aria-label="Close">
            ✕
          </button>
        </header>

        <section className="flex flex-col gap-2">
          <label className="text-xs uppercase tracking-wide text-text-muted" htmlFor="universe-name">
            This universe
          </label>
          <div className="flex gap-2">
            <input
              id="universe-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-3 py-1.5 text-sm"
            />
            <button
              onClick={() => rename.mutate(name.trim())}
              disabled={!name.trim() || name === universeName || rename.isPending}
              className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-40"
            >
              Rename
            </button>
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-xs uppercase tracking-wide text-text-muted">In orbit</h3>
          {planets.map((planet) => (
            <PlanetRow
              key={planet.id}
              planet={planet}
              looks={looks}
              onPatch={(body) => patch.mutate({ id: planet.id, body })}
            />
          ))}
          <div className="flex gap-2">
            <input
              value={newPlanet}
              onChange={(e) => setNewPlanet(e.target.value)}
              placeholder="A new planet"
              className="flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-3 py-1.5 text-sm"
            />
            <button
              onClick={() => add.mutate(newPlanet.trim())}
              disabled={!newPlanet.trim() || add.isPending}
              className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-40"
            >
              Add
            </button>
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="text-xs uppercase tracking-wide text-text-muted">Atmosphere</h3>

          <div className="flex gap-2">
            <input
              value={atmosphere.state.url}
              onChange={(e) => atmosphere.update({ url: e.target.value })}
              placeholder="A sound file to play under it all"
              className="flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-3 py-1.5 text-sm"
            />
            <button
              onClick={() => (atmosphere.state.playing ? atmosphere.pause() : void atmosphere.play())}
              disabled={false}
              className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-40"
            >
              {atmosphere.state.playing ? 'Pause' : 'Play'}
            </button>
          </div>
          {atmosphere.error && <p className="text-xs text-text-muted">{atmosphere.error}</p>}

          <Slider
            label="Track"
            value={atmosphere.state.volume}
            onChange={(volume) => atmosphere.update({ volume })}
          />
          <Slider
            label="White noise"
            value={atmosphere.state.noiseVolume}
            onChange={(noiseVolume) => atmosphere.update({ noiseVolume })}
          />
          <Slider
            label="Noise tone"
            value={atmosphere.state.noiseTone}
            onChange={(noiseTone) => atmosphere.update({ noiseTone })}
            hint="deep · bright"
          />
        </section>
      </div>
    </div>
  );
}

function PlanetRow({
  planet,
  looks,
  onPatch,
}: {
  planet: Planet;
  looks: number[];
  onPatch: (body: { name?: string; importanceWeight?: number; colourSeed?: number; archived?: boolean }) => void;
}) {
  const [name, setName] = useState(planet.name);
  const current = planetTraits(planet.colourSeed).palette.equator;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => name.trim() && name !== planet.name && onPatch({ name: name.trim() })}
        className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-border bg-transparent px-3 py-1.5 text-sm"
      />
      <label className="flex items-center gap-1 text-xs text-text-muted">
        size
        <input
          type="number"
          min={1}
          max={10}
          defaultValue={planet.importanceWeight}
          onBlur={(e) => onPatch({ importanceWeight: Number(e.target.value) })}
          className="w-14 rounded-[var(--radius-sm)] border border-border bg-transparent px-2 py-1 text-sm"
        />
      </label>
      <div className="flex gap-1">
        {looks.map((seed) => {
          const colour = planetTraits(seed).palette.equator;
          return (
            <button
              key={seed}
              onClick={() => onPatch({ colourSeed: seed })}
              aria-label={`Set ${planet.name} to this look`}
              className="h-5 w-5 rounded-full"
              style={{
                background: colour,
                outline: colour === current ? '2px solid var(--text)' : 'none',
                outlineOffset: '1px',
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function Slider({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  hint?: string;
}) {
  return (
    <label className="flex items-center gap-3 text-sm">
      <span className="w-24 shrink-0 text-text-muted">{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1"
      />
      <span className="w-20 shrink-0 text-right text-xs text-text-muted">
        {hint ?? `${Math.round(value * 100)}%`}
      </span>
    </label>
  );
}
